import { FilterSelect } from '@/components/filter-select'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button as UiButton } from '@/components/ui/button'
import { CheckCircle2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Field, PurchasingDialog } from '@Purchasing/ui/PurchasingDialog'
import { MoneyInput } from '@Purchasing/ui/MoneyInput'
import {
    FinanceSummary,
    PaymentMethod,
    PaymentStatus,
    PaymentType,
    PurchasePayment,
} from '@Purchasing/Domain/Model/PurchaseProcess'
import { MarkPaymentPaidPayload } from '@Purchasing/Data/DataSource/API/Entity/PurchaseProcess'
import { roles } from '@Purchasing/roles'
import { formatDateOnly } from '@Purchasing/Utils/date'
import { centsToInput, formatBRL, realToCents } from '@Purchasing/Utils/money'
import { format } from 'date-fns'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

// Labels humanos centralizados (Fase 5.0.2 §22/§23)
import {
    paymentMethodLabel as paymentMethodLabelCentral,
    paymentStatusLabel as paymentStatusLabelCentral,
    paymentTypeLabel as paymentTypeLabelCentral,
} from '@Purchasing/presentation/purchasing-labels'

export const paymentStatusLabel = paymentStatusLabelCentral
export const paymentTypeLabel = paymentTypeLabelCentral
export const paymentMethodLabel = paymentMethodLabelCentral

export const financeStatusLabel: Record<string, string> = {
    NOT_PLANNED: 'Sem pagamentos planejados',
    UNPAID: 'A pagar',
    PARTIALLY_PAID: 'Parcialmente pago',
    PAID: 'Pago integralmente',
}

const chip =
    'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold'

function statusChip(payment: PurchasePayment) {
    if (payment.overdue) {
        return (
            <span className={`${chip} bg-warning-50 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400`}>
                Atrasado
            </span>
        )
    }
    const map: Record<PaymentStatus, string> = {
        PAID: 'bg-od-primary-100 text-od-primary-800 dark:bg-od-primary-900/40 dark:text-od-primary-200',
        PENDING:
            'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
        CANCELED:
            'bg-od-secondary-50 text-od-secondary-500 dark:bg-od-secondary-900/20 dark:text-od-secondary-200',
    }
    return (
        <span className={`${chip} ${map[payment.status] ?? ''}`}>
            {paymentStatusLabel[payment.status]}
        </span>
    )
}

// ---------------------------------------------------------------------------
// Resumo com barra de progresso textual (§28)
// ---------------------------------------------------------------------------

interface FinanceSummaryCardProps {
    summary: FinanceSummary
}

export const FinanceSummaryCard = ({ summary }: FinanceSummaryCardProps) => {
    const pct =
        summary.totalCents > 0
            ? Math.min(
                  100,
                  Math.round((summary.paidCents / summary.totalCents) * 100)
              )
            : 0

    return (
        <div className="space-y-3">
            <div
                className="flex flex-wrap items-center gap-2"
                data-testid="finance-status"
            >
                <FinanceStatusBadge summary={summary} />
                {summary.paymentsOverdue > 0 && (
                    <span
                        className="text-xs font-medium text-warning-500"
                        data-testid="overdue-count"
                    >
                        ⚠ {summary.paymentsOverdue}{' '}
                        {summary.paymentsOverdue === 1
                            ? 'pagamento atrasado'
                            : 'pagamentos atrasados'}
                    </span>
                )}
            </div>

            <div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                        className="h-full rounded-full bg-primary dark:bg-primary"
                        style={{ width: `${pct}%` }}
                        data-testid="paid-progress-bar"
                    />
                </div>
                <p
                    className="mt-1 text-xs text-gray-500 dark:text-gray-400"
                    data-testid="paid-progress-text"
                >
                    {pct}% pago —{' '}
                    <strong className="text-gray-800 dark:text-gray-200">
                        {formatBRL(summary.paidCents)}
                    </strong>{' '}
                    de {formatBRL(summary.totalCents)}
                </p>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
                <Total label="Total da compra" cents={summary.totalCents} testId="finance-total" />
                <Total label="Planejado" cents={summary.plannedCents} testId="finance-planned" />
                <Total label="Pago" cents={summary.paidCents} strong testId="finance-paid" />
                <Total label="Saldo a pagar" cents={summary.outstandingCents} testId="finance-outstanding" />
            </dl>

            {summary.financeStatus === 'PAID' && (
                <p
                    className="rounded-md bg-od-primary-50 px-3 py-1.5 text-xs font-medium text-od-primary-800 dark:bg-od-primary-900/40 dark:text-od-primary-200"
                    data-testid="fully-paid-banner"
                >
                    ✓ Compra paga integralmente
                </p>
            )}

            {summary.remainingToPlanCents > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Falta planejar{' '}
                    <strong>
                        {formatBRL(summary.remainingToPlanCents)}
                    </strong>{' '}
                    do total.
                </p>
            )}
        </div>
    )
}

export const FinanceStatusBadge = ({
    summary,
}: {
    summary: FinanceSummary
}) => {
    const tone =
        summary.financeStatus === 'PAID'
            ? 'bg-od-primary-100 text-od-primary-800 dark:bg-od-primary-900/40 dark:text-od-primary-200'
            : summary.financeStatus === 'PARTIALLY_PAID'
              ? 'bg-warning-100 text-warning-600 dark:bg-warning-900/40 dark:text-warning-300'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone}`}>
            {financeStatusLabel[summary.financeStatus] ?? summary.financeStatus}
        </span>
    )
}

const Total = ({
    label,
    cents,
    strong,
    testId,
}: {
    label: string
    cents: number
    strong?: boolean
    testId?: string
}) => (
    <div>
        <dt className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            {label}
        </dt>
        <dd
            data-testid={testId}
            className={
                strong
                    ? 'font-semibold text-gray-900 dark:text-gray-100'
                    : 'text-gray-700 dark:text-gray-200'
            }
        >
            {formatBRL(cents)}
        </dd>
    </div>
)

// ---------------------------------------------------------------------------
// Tabela/lista compacta de pagamentos (§29/§30)
// ---------------------------------------------------------------------------

interface PaymentsTableProps {
    payments: PurchasePayment[]
    onPayClick?: (payment: PurchasePayment) => void
    onCancelClick?: (payment: PurchasePayment) => void
}

export const PaymentsTable = ({
    payments,
    onPayClick,
    onCancelClick,
}: PaymentsTableProps) => {
    if (!payments.length)
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400">
                Nenhum pagamento registrado ainda.
            </p>
        )

    return (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {payments.map((p) => (
                <li
                    key={p._id}
                    className="flex items-center justify-between gap-3 py-2"
                    data-testid={`payment-row-${p._id}`}
                >
                    <div className="min-w-0">
                        <p className="truncate text-sm text-gray-900 dark:text-gray-100">
                            {paymentTypeLabel[p.type] ?? p.type}
                            {p.notes ? (
                                <span
                                    className="ml-1 text-xs text-gray-400"
                                    title={p.notes}
                                >
                                    · {p.notes}
                                </span>
                            ) : null}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {p.dueDate
                                ? `Vence ${formatDateOnly(p.dueDate)}`
                                : 'Sem vencimento'}
                            {' • '}
                            {formatBRL(p.amountCents)}
                            {p.paidAt
                                ? ` • pago em ${format(
                                      new Date(p.paidAt),
                                      'dd/MM/yyyy'
                                  )}`
                                : ''}
                        </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                        {statusChip(p)}
                        {p.status === 'PENDING' && (
                            <>
                                <PermissionGate
                                    permission={roles.payment.permissions.pay.key}
                                >
                                    <UiButton
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-8 gap-1"
                                        onClick={() => onPayClick?.(p)}
                                        data-testid={`pay-payment-${p._id}`}
                                    >
                                        <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />
                                        Dar baixa
                                    </UiButton>
                                </PermissionGate>
                                <PermissionGate
                                    permission={
                                        roles.payment.permissions.cancel.key
                                    }
                                >
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <UiButton
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                className="h-8 text-muted-foreground hover:bg-od-secondary-50 hover:text-od-secondary-600"
                                                data-testid={`cancel-payment-${p._id}`}
                                            >
                                                Cancelar
                                            </UiButton>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent className="rounded-card">
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Cancelar este pagamento?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    {paymentTypeLabel[p.type] ?? p.type} de {formatBRL(p.amountCents)}. O valor volta para o saldo a planejar. Não é possível desfazer.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel className="rounded-full">Voltar</AlertDialogCancel>
                                                <AlertDialogAction
                                                    className="rounded-full bg-destructive text-destructive-foreground hover:bg-od-secondary-500"
                                                    onClick={() => onCancelClick?.(p)}
                                                    data-testid={`confirm-cancel-payment-${p._id}`}
                                                >
                                                    Cancelar pagamento
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </PermissionGate>
                            </>
                        )}
                    </div>
                </li>
            ))}
        </ul>
    )
}

import { PermissionWrapper } from '@Core/Components/PermissionWrapper/PermissionWrapper'
const PermissionGate = PermissionWrapper

// ---------------------------------------------------------------------------
// Novo pagamento (§31/§32)
// ---------------------------------------------------------------------------

const makeNewPaymentSchema = (remainingToPlanCents: number) =>
    z.object({
        type: z.enum(['ADVANCE', 'INSTALLMENT', 'FINAL', 'OTHER']),
        amount: z
            .string()
            .min(1, 'Informe o valor')
            .refine((value) => realToCents(value) > 0, 'O valor precisa ser maior que zero')
            .refine(
                (value) => realToCents(value) <= remainingToPlanCents,
                `O valor passa do saldo disponível (${formatBRL(remainingToPlanCents)})`
            ),
        dueDate: z.string().optional(),
        notes: z.string().optional(),
    })

type NewPaymentValues = z.infer<ReturnType<typeof makeNewPaymentSchema>>

// Rótulos vêm do mapa central: a lista e o modal mostram o mesmo nome.
const paymentTypeOptions = (['ADVANCE', 'INSTALLMENT', 'FINAL', 'OTHER'] as const).map((value) => ({
    value,
    label: paymentTypeLabelCentral[value],
}))

const NEW_PAYMENT_FORM = 'new-payment-form'

interface NewPaymentModalProps {
    isOpen: boolean
    onClose?: () => void
    remainingToPlanCents: number
    submitting?: boolean
    /** Erro devolvido pela API, exibido dentro do modal. */
    error?: string | null
    onSubmit: (payload: {
        type: PaymentType
        amountCents: number
        dueDate?: string
        notes?: string
    }) => void
}

export const NewPaymentModal = ({
    isOpen,
    onClose,
    remainingToPlanCents,
    submitting,
    error,
    onSubmit,
}: NewPaymentModalProps) => {
    const schema = useMemo(() => makeNewPaymentSchema(remainingToPlanCents), [remainingToPlanCents])
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        control,
        formState: { errors },
    } = useForm<NewPaymentValues>({
        resolver: zodResolver(schema),
        defaultValues: { type: 'INSTALLMENT', amount: '' },
    })

    useEffect(() => {
        if (isOpen)
            reset({ type: 'INSTALLMENT', amount: '', dueDate: '', notes: '' })
    }, [isOpen, reset])

    const submit = handleSubmit((values) => {
        onSubmit({
            type: values.type,
            amountCents: realToCents(values.amount),
            dueDate: values.dueDate || undefined,
            notes: values.notes || undefined,
        })
    })

    return (
        <PurchasingDialog
            isOpen={isOpen}
            onClose={onClose}
            eyebrow="Financeiro"
            title="Novo pagamento"
            description="Planeje uma parcela da compra. A baixa é feita depois, quando o pagamento acontecer."
            footer={
                <>
                    <UiButton type="button" variant="ghost" onClick={onClose}>Cancelar</UiButton>
                    <UiButton type="submit" form={NEW_PAYMENT_FORM} disabled={submitting} data-testid="confirm-new-payment" className="min-w-[160px]">
                        {submitting ? 'Salvando…' : 'Criar pagamento'}
                    </UiButton>
                </>
            }
        >
            <form id={NEW_PAYMENT_FORM} onSubmit={submit} noValidate className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-secondary px-4 py-3 text-sm">
                    <span className="text-muted-foreground">
                        Disponível para alocar{' '}
                        <strong className="font-display text-base text-brand-deep" data-testid="remaining-to-plan">
                            {formatBRL(remainingToPlanCents)}
                        </strong>
                    </span>
                    <UiButton
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setValue('amount', centsToInput(remainingToPlanCents), { shouldValidate: true })}
                        data-testid="use-remaining"
                    >
                        Usar saldo restante
                    </UiButton>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Tipo">
                        <FilterSelect
                            label="Tipo do pagamento"
                            value={watch('type')}
                            onChange={(value) => setValue('type', value as NewPaymentValues['type'])}
                            options={paymentTypeOptions}
                            className="h-10 w-full rounded-md"
                        />
                    </Field>
                    <Field label="Valor" htmlFor="new-payment-amount" error={errors.amount?.message}>
                        <Controller
                            control={control}
                            name="amount"
                            render={({ field }) => (
                                <MoneyInput
                                    id="new-payment-amount"
                                    name="amount"
                                    aria-label="Valor do pagamento"
                                    value={field.value}
                                    onChange={field.onChange}
                                    invalid={Boolean(errors.amount)}
                                    data-testid="new-payment-amount"
                                />
                            )}
                        />
                    </Field>
                    <Field label="Vencimento" htmlFor="new-payment-due">
                        <Input id="new-payment-due" type="date" {...register('dueDate')} data-testid="new-payment-due" />
                    </Field>
                    <Field label="Observações" htmlFor="new-payment-notes">
                        <Input id="new-payment-notes" {...register('notes')} placeholder="Ex.: 50% na aprovação da arte" data-testid="new-payment-notes" />
                    </Field>
                </div>

                {error && <ApiError message={error} />}
            </form>
        </PurchasingDialog>
    )
}

const ApiError = ({ message }: { message: string }) => (
    <p className="rounded-r-md border-l-[3px] border-l-brand-pink bg-od-secondary-50 px-3 py-2 text-sm text-od-secondary-600" role="alert">
        {message}
    </p>
)

// ---------------------------------------------------------------------------
// Dar baixa (§33)
// ---------------------------------------------------------------------------

const markPaidSchema = z.object({
    paidAt: z.string().min(1),
    paymentMethod: z.enum(['PIX', 'BOLETO', 'BANK_TRANSFER', 'CARD', 'CASH', 'OTHER']),
    transactionReference: z.string().optional(),
})

const paymentMethodOptions = [
    { value: 'PIX', label: 'PIX' },
    { value: 'BOLETO', label: 'Boleto' },
    { value: 'BANK_TRANSFER', label: 'Transferência' },
    { value: 'CARD', label: 'Cartão' },
    { value: 'CASH', label: 'Dinheiro' },
    { value: 'OTHER', label: 'Outro' },
]

interface MarkPaidModalProps {
    isOpen: boolean
    onClose?: () => void
    payment: PurchasePayment | null
    submitting?: boolean
    /** Erro devolvido pela API, exibido dentro do modal. */
    error?: string | null
    onConfirm: (payload: MarkPaymentPaidPayload) => void
}

const MARK_PAID_FORM = 'mark-paid-form'

export const MarkPaidModal = ({
    isOpen,
    onClose,
    payment,
    submitting,
    error,
    onConfirm,
}: MarkPaidModalProps) => {
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(markPaidSchema),
        defaultValues: {
            paidAt: format(new Date(), 'yyyy-MM-dd'),
            paymentMethod: 'PIX',
            transactionReference: '',
        },
    })

    useEffect(() => {
        if (isOpen)
            reset({
                paidAt: format(new Date(), 'yyyy-MM-dd'),
                paymentMethod: 'PIX',
                transactionReference: '',
            })
    }, [isOpen, reset])

    if (!payment) return null

    const submit = handleSubmit((values) => {
        onConfirm({
            paidAt: new Date(`${values.paidAt}T12:00:00`).toISOString(),
            paymentMethod: values.paymentMethod,
            transactionReference: values.transactionReference || undefined,
        })
    })

    return (
        <PurchasingDialog
            isOpen={isOpen}
            onClose={onClose}
            eyebrow={`Financeiro · ${paymentTypeLabelCentral[payment.type] ?? 'Pagamento'}`}
            title="Confirmar pagamento"
            description="Registra a baixa deste pagamento. Depois de confirmado, ele não pode ser cancelado."
            footer={
                <>
                    <UiButton type="button" variant="ghost" onClick={onClose}>Voltar</UiButton>
                    <UiButton type="submit" form={MARK_PAID_FORM} disabled={submitting} data-testid="confirm-mark-paid" className="min-w-[180px]">
                        {submitting ? 'Confirmando…' : 'Confirmar pagamento'}
                    </UiButton>
                </>
            }
        >
            <form id={MARK_PAID_FORM} onSubmit={submit} noValidate className="flex flex-col gap-4">
                <div className="flex items-baseline justify-between rounded-md bg-secondary px-4 py-3">
                    <span className="text-sm text-muted-foreground">Valor</span>
                    <strong className="font-display text-xl text-brand-deep tabular-nums" data-testid="mark-paid-amount">
                        {formatBRL(payment.amountCents)}
                    </strong>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Data do pagamento" htmlFor="mark-paid-date">
                        <Input id="mark-paid-date" type="date" {...register('paidAt')} data-testid="mark-paid-date" />
                    </Field>
                    <Field label="Método" error={errors.paymentMethod ? 'Escolha o método' : undefined}>
                        <FilterSelect
                            label="Método de pagamento"
                            value={watch('paymentMethod')}
                            onChange={(value) => setValue('paymentMethod', value as any)}
                            options={paymentMethodOptions}
                            className="h-10 w-full rounded-md"
                        />
                    </Field>
                    <Field label="Referência (opcional)" htmlFor="mark-paid-ref" className="sm:col-span-2">
                        <Input
                            id="mark-paid-ref"
                            {...register('transactionReference')}
                            placeholder="ID do PIX, número do boleto…"
                            data-testid="mark-paid-ref"
                        />
                    </Field>
                </div>
                {error && <ApiError message={error} />}
            </form>
        </PurchasingDialog>
    )
}
